import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useCallback } from 'react'
import { adApi } from '../services/adApi'
import { getTenantId } from '../config/tenant'

const CONFIGS_KEY = (tenantId) => ['adConfigs', tenantId]
const RULES_KEY = (configId) => ['adRules', configId]
const AD_USERS_KEY = (tenantId) => ['adUsers', tenantId]

export const useAdConfigs = () => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: CONFIGS_KEY(tenantId),
    queryFn: () => adApi.getConfigs(),
    enabled: !!tenantId,
  })
}

export const useCreateAdConfig = () => {
  const queryClient = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: (data) => adApi.createConfig(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONFIGS_KEY(tenantId) }),
  })
}

export const useUpdateAdConfig = () => {
  const queryClient = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: ({ id, data }) => adApi.updateConfig(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONFIGS_KEY(tenantId) }),
  })
}

export const useDeleteAdConfig = () => {
  const queryClient = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: (id) => adApi.deleteConfig(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONFIGS_KEY(tenantId) }),
  })
}

export const useTestAdConnection = () => {
  const queryClient = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: (configId) => adApi.testConnection(configId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONFIGS_KEY(tenantId) }),
  })
}

export const useAdRules = (configId) =>
  useQuery({
    queryKey: RULES_KEY(configId),
    queryFn: () => adApi.getRules(configId),
    enabled: !!configId,
  })

export const useCreateAdRule = (configId) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => adApi.createRule(configId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RULES_KEY(configId) }),
  })
}

export const useUpdateAdRule = (configId) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ruleId, data }) => adApi.updateRule(configId, ruleId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RULES_KEY(configId) }),
  })
}

export const useDeleteAdRule = (configId) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ruleId) => adApi.deleteRule(configId, ruleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RULES_KEY(configId) }),
  })
}

export const useAdUsers = () => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: AD_USERS_KEY(tenantId),
    queryFn: () => adApi.getAdUsers(),
    enabled: !!tenantId,
  })
}

/** Busca grupos no AD — chamada manual via mutação para não disparar no mount */
export const useSearchAdGroups = (configId) => {
  const [results, setResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)

  const search = useCallback(async (q = '') => {
    if (!configId) return
    setIsSearching(true)
    setSearchError(null)
    try {
      const data = await adApi.searchGroups(configId, q)
      setResults(data)
    } catch (err) {
      setSearchError(err?.message || 'Erro ao buscar grupos no AD')
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }, [configId])

  return { results, isSearching, searchError, search, setResults }
}
